import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { RequestKindToggle, stepsFor } from "./RequestKindToggle";
import { StepLamp } from "./StepLamps";

// Photos beside the steps on the Metodo page: the two that were on "Chi siamo" and the two sent by the studio
const STEP_PHOTOS = [
  { src: "/founders/falsi-colori.jpg", alt: "Alessia e Alessandra al lavoro su una simulazione DIALux evo a falsi colori", pos: "60% 80%" },
  { src: "/founders/studio-1.jpg", alt: "Alessia e Alessandra al tavolo di lavoro, sotto le lampade a soffitto", pos: "50% 55%" },
  { src: "/media/metodo-3.jpg", alt: "FArLight, il progetto della luce", pos: "50% 50%" },
  { src: "/media/metodo-4.jpg", alt: "FArLight, il progetto della luce", pos: "50% 50%" },
];

// "Il nostro metodo": how the Lighting Concept form works, as numbered steps (home page teaser + /metodo page)
export const MethodSteps = ({ teaser = false }) => {
  const { t } = useLanguage();
  const m = t.methodPage;
  const [kind, setKind] = useState("concept");
  return (
    <section data-testid={teaser ? "home-method" : "method-steps"} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div className="max-w-2xl" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft mb-5">{m.stepsOverline}</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight text-ink">{m.stepsTitle}</h2>
        </motion.div>
        <div className="mt-10"><RequestKindToggle t={t} value={kind} onChange={setKind} testid={teaser ? "home-method-kind" : "method-kind"} /></div>
        {teaser ? (
          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {stepsFor(t, kind).map((step, i) => (
              <motion.li key={`${i}-${i === 3 ? kind : ""}`} data-testid={`method-step-${i}`} className="border-t border-ink/15 pt-5"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <span className="font-mono text-[11px] text-amberdark">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 text-lg font-medium text-ink">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-inksoft">{step.desc}</p>
              </motion.li>
            ))}
          </ol>
        ) : (
          // Metodo page: each step beside a photo, alternating sides
          <ol className="mt-14 space-y-16 lg:space-y-24">
            {stepsFor(t, kind).map((step, i) => {
              const photo = STEP_PHOTOS[i % STEP_PHOTOS.length];
              const flip = i % 2 === 1;
              return (
                <motion.li key={`${i}-${i === 3 ? kind : ""}`} data-testid={`method-step-${i}`} className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center"
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
                  <div className={`lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}>
                    <StepLamp index={i} testid={`method-step-lamp-${i}`} />
                    <h3 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight leading-tight text-ink">{step.title}</h3>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-inksoft">{step.desc}</p>
                  </div>
                  <div className={`group relative lg:col-span-6 aspect-[4/3] overflow-hidden rounded-sm bg-black ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
                    <img data-testid={`method-step-photo-${i}`} src={photo.src} alt={photo.alt} loading="lazy" className="bw-hover absolute inset-0 w-full h-full object-cover" style={{ objectPosition: photo.pos }} />
                  </div>
                </motion.li>
              );
            })}
          </ol>
        )}
        <div className="mt-14">
          {teaser ? (
            <Link to="/metodo" data-testid="home-method-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink border-b border-amberdark pb-1 hover:text-amberdark transition-colors">
              {m.homeCta}<ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          ) : (
            <Link to={`/richiedi-concept?plan=${kind === "executive" ? "advanced" : "base"}`} data-testid="method-form-cta" className="inline-flex items-center gap-3 bg-ink text-cream hover:bg-espresso rounded-sm px-8 h-12 text-xs font-semibold uppercase tracking-[0.15em]">
              {m.cta}<span aria-hidden="true">&rarr;</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
